import React, { useState } from 'react';
import { Mail, Send, Users, CheckCircle, AlertCircle, Loader, Clock, Edit3, ChevronDown, ChevronUp, BarChart2, FlaskConical, ShieldCheck } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const DEFAULT_SUBJECT = "We miss you! Come back to MyBodyToday 💙";
const DEFAULT_BODY = `Hi {{first_name}},

We noticed you haven't visited MyBodyToday in a while — and we wanted to reach out personally.

Your health journey matters, and MyBodyToday is here to help you track symptoms, prepare for doctor visits, and stay on top of your wellbeing every day.

👉 Sign back in and pick up where you left off.

🎉 Here's something worth knowing:

• MyBodyToday is completely free to use
• By joining our referral programme, you can unlock full access to all premium features at no cost
• Simply share your referral link with friends and family to enjoy everything the app has to offer!

Did you know you can also add MyBodyToday directly to your phone's home screen for quick, one-tap access?

📱 On iPhone (Safari): Tap the Share icon → "Add to Home Screen"
🤖 On Android (Chrome): Tap the three-dot menu → "Add to Home Screen"

We're glad you're part of our community. See you soon!

Warm regards,
The MyBodyToday Team`;

const ReEngagementEmailPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [subjectError, setSubjectError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [testStatus, setTestStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  const getTodayDate = () => {
    const d = new Date();
    return d?.toISOString()?.split('T')?.[0];
  };

  const validateTemplate = () => {
    let valid = true;
    if (!subject?.trim()) {
      setSubjectError('Subject line is required.');
      valid = false;
    } else {
      setSubjectError('');
    }
    if (!body?.trim() || body?.trim()?.length < 20) {
      setBodyError('Email body must be at least 20 characters.');
      valid = false;
    } else {
      setBodyError('');
    }
    return valid;
  };

  const handleSendTestEmail = async () => {
    if (!validateTemplate()) {
      setTemplateOpen(true);
      return;
    }
    const recipientEmail = testEmail?.trim() || user?.email;
    if (!recipientEmail) {
      setTestStatus('error');
      setTestResult({ error: 'Please enter a recipient email address.' });
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(recipientEmail)) {
      setTestStatus('error');
      setTestResult({ error: 'Please enter a valid email address.' });
      return;
    }
    setTestStatus('loading');
    setTestResult(null);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const token = sessionData?.session?.access_token;
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-reengagement-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject: `[TEST] ${subject?.trim()}`,
          body: body?.trim(),
          target_emails: [recipientEmail],
          is_test: true,
        }),
      });
      const result = await response?.json();
      if (!response?.ok) {
        // Surface the full error from the response body
        throw new Error(result?.error || result?.message || `HTTP ${response.status}: Send failed`);
      }
      if (result?.failed > 0 && result?.sent === 0) {
        // All recipients failed — extract the first error for display
        const firstError = result?.errors?.[0] || 'Send failed — check Resend configuration';
        throw new Error(firstError);
      }
      setTestStatus('success');
      setTestResult({ email: recipientEmail, sent: result?.sent });
    } catch (err) {
      setTestStatus('error');
      setTestResult({ error: err?.message || 'Failed to send test email' });
    }
  };

  const handleSendEmails = async () => {
    if (!validateTemplate()) {
      setConfirmOpen(false);
      setTemplateOpen(true);
      return;
    }
    setConfirmOpen(false);
    setStatus('loading');
    setResult(null);

    try {
      const payload = {
        subject: subject?.trim(),
        body: body?.trim(),
        ...(sendMode === 'scheduled' && scheduledDate
          ? { scheduledAt: `${scheduledDate}T${scheduledTime}:00` }
          : {})
      };

      const { data, error } = await supabase?.functions?.invoke('send-reengagement-email', {
        method: 'POST',
        body: payload
      });

      if (error) throw error;

      if (data?.success) {
        setStatus('success');
        setResult({ ...data, scheduled: sendMode === 'scheduled' && !!scheduledDate });
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (err) {
      setStatus('error');
      setResult({ error: err?.message || 'Failed to send re-engagement emails' });
    }
  };

  const handleConfirm = () => {
    if (!validateTemplate()) {
      setTemplateOpen(true);
      return;
    }
    setConfirmOpen(true);
  };

  const scheduledLabel = sendMode === 'scheduled' && scheduledDate
    ? `Scheduled for ${scheduledDate} at ${scheduledTime}`
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Panel Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-4 flex items-center gap-3">
        <div className="bg-white/20 rounded-lg p-2">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-base">Re-Engagement Email Campaign</h3>
          <p className="text-teal-100 text-xs mt-0.5">Send a reminder to all registered users</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/dns-setup')}
            className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
            title="SPF/DKIM DNS Setup"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            DNS Setup
          </button>
          <button
            onClick={() => navigate('/email-campaign-analytics')}
            className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            <BarChart2 className="w-3.5 h-3.5" />
            Analytics
          </button>
        </div>
      </div>
      {/* Panel Body */}
      <div className="p-5 space-y-5">
        <p className="text-gray-600 text-sm leading-relaxed">
          Send a personalised re-engagement email to <strong>all registered users</strong> reminding them to sign back in. The email includes a <strong>Sign In CTA</strong> and a step-by-step guide on how to add MyBodyToday to their home screen.
        </p>

        {/* ── Send Timing ── */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Send Timing</span>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex gap-3">
              <button
                onClick={() => setSendMode('now')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border transition-all ${
                  sendMode === 'now' ?'bg-teal-600 text-white border-teal-600' :'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                }`}
              >
                Send Now
              </button>
              <button
                onClick={() => setSendMode('scheduled')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border transition-all ${
                  sendMode === 'scheduled' ?'bg-teal-600 text-white border-teal-600' :'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                }`}
              >
                Schedule for Later
              </button>
            </div>

            {sendMode === 'scheduled' && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Date</label>
                  <input
                    type="date"
                    min={getTodayDate()}
                    value={scheduledDate}
                    onChange={e => setScheduledDate(e?.target?.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Time</label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={e => setScheduledTime(e?.target?.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {scheduledLabel && (
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
                <Clock className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
                <span className="text-teal-700 text-xs font-medium">{scheduledLabel}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Email Template Editor ── */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setTemplateOpen(v => !v)}
            className="w-full bg-gray-50 px-4 py-3 flex items-center gap-2 border-b border-gray-200 hover:bg-gray-100 transition-colors"
          >
            <Edit3 className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700 flex-1 text-left">Customise Email Template</span>
            {templateOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>

          {templateOpen && (
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                <p className="text-blue-700 text-xs">
                  Use <code className="bg-blue-100 px-1 rounded font-mono">{'{{first_name}}'}</code> to personalise with the recipient's first name.
                </p>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Subject Line</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => { setSubject(e?.target?.value); setSubjectError(''); }}
                  placeholder="Enter email subject..."
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent ${subjectError ? 'border-red-400' : 'border-gray-200'}`}
                />
                {subjectError && <p className="text-red-500 text-xs mt-1">{subjectError}</p>}
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email Body</label>
                <textarea
                  value={body}
                  onChange={e => { setBody(e?.target?.value); setBodyError(''); }}
                  rows={12}
                  placeholder="Write your email message here..."
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent resize-y ${bodyError ? 'border-red-400' : 'border-gray-200'}`}
                />
                {bodyError && <p className="text-red-500 text-xs mt-1">{bodyError}</p>}
                <p className="text-gray-400 text-xs mt-1 text-right">{body?.length} characters</p>
              </div>

              <button
                onClick={() => { setSubject(DEFAULT_SUBJECT); setBody(DEFAULT_BODY); setSubjectError(''); setBodyError(''); }}
                className="text-xs text-teal-600 hover:text-teal-800 underline underline-offset-2 transition-colors"
              >
                Reset to default template
              </button>
            </div>
          )}
        </div>

        {/* ── Send Test Email ── */}
        <div className="border border-violet-200 rounded-xl overflow-hidden bg-violet-50/40">
          <div className="px-4 py-3 flex items-center gap-2 border-b border-violet-200 bg-violet-50">
            <FlaskConical className="w-4 h-4 text-violet-600" />
            <span className="text-sm font-semibold text-violet-800">Test Before Sending</span>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-gray-600 leading-relaxed">
              Send a test email to verify delivery and formatting before launching the campaign.
              The subject will be prefixed with <span className="font-mono bg-gray-100 px-1 rounded text-xs">[TEST]</span>.
            </p>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Send test to</label>
              <input
                type="email"
                value={testEmail}
                onChange={e => { setTestEmail(e?.target?.value); if (testStatus === 'error') { setTestStatus('idle'); setTestResult(null); } }}
                placeholder={user?.email || 'Enter email address…'}
                className="w-full border border-violet-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent bg-white"
              />
              <p className="text-gray-400 text-xs mt-1">
                Enter any working inbox (e.g. a Gmail address) to confirm delivery. Leave blank to use <strong>{user?.email || 'your account email'}</strong>.
              </p>
            </div>

            {testStatus === 'success' && testResult && (
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-800 text-xs font-medium">
                  Test email sent to <strong>{testResult?.email}</strong>. Check your inbox (and spam folder).
                </p>
              </div>
            )}

            {testStatus === 'error' && testResult && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-red-800 text-xs font-semibold mb-1">Test email failed</p>
                  <p className="text-red-700 text-xs break-words">{testResult?.error}</p>
                  {testResult?.error?.includes('onboarding@resend.dev') || testResult?.error?.includes('only send') || testResult?.error?.includes('validation_error') || testResult?.error?.includes('403') ? (
                    <p className="text-amber-700 text-xs mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      <strong>Why this happens:</strong> Resend's <code className="font-mono bg-amber-100 px-0.5 rounded">onboarding@resend.dev</code> sender can only deliver to the email address that owns your Resend account. To send to any inbox, <a href="/dns-setup" className="underline font-semibold">verify mybodytoday1.com in Resend</a> and update the <code className="font-mono bg-amber-100 px-0.5 rounded">from</code> address in the edge function.
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            <button
              onClick={handleSendTestEmail}
              disabled={testStatus === 'loading'}
              className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              {testStatus === 'loading' ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Sending test email…
                </>
              ) : (
                <>
                  <FlaskConical className="w-4 h-4" />
                  Send test email
                </>
              )}
            </button>
          </div>
        </div>

        {/* Result feedback */}
        {status === 'success' && result && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-800 font-semibold text-sm">
                {result?.scheduled ? 'Campaign scheduled successfully!' : 'Campaign sent successfully!'}
              </p>
              {!result?.scheduled && (
                <p className="text-green-700 text-sm mt-1">
                  ✅ {result?.sent} sent &nbsp;·&nbsp; ❌ {result?.failed} failed &nbsp;·&nbsp; 📋 {result?.total} total
                </p>
              )}
              {result?.scheduled && (
                <p className="text-green-700 text-sm mt-1">Your campaign will be sent at the scheduled time.</p>
              )}
            </div>
          </div>
        )}

        {status === 'error' && result && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-semibold text-sm">Failed to send campaign</p>
              <p className="text-red-700 text-sm mt-1">{result?.error}</p>
            </div>
          </div>
        )}

        {/* Confirm dialog */}
        {confirmOpen ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3 mb-4">
              <Users className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 font-semibold text-sm">Confirm bulk email send</p>
                <p className="text-amber-700 text-sm mt-1">
                  This will send an email to <strong>every registered user</strong> in the database.
                  {sendMode === 'scheduled' && scheduledDate
                    ? ` It will be sent on ${scheduledDate} at ${scheduledTime}.`
                    : ' It will be sent immediately.'}
                  {' '}This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSendEmails}
                className="flex-1 flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
                {sendMode === 'scheduled' && scheduledDate ? 'Yes, Schedule It' : 'Yes, Send Now'}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={handleConfirm}
            disabled={status === 'loading'}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-sm text-sm"
          >
            {status === 'loading' ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Sending emails…
              </>
            ) : sendMode === 'scheduled' && scheduledDate ? (
              <>
                <Clock className="w-4 h-4" />
                Schedule Campaign
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Re-Engagement Email to All Users
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ReEngagementEmailPanel;
